import { datedPrice } from "@/content/vendor-prices";
import { createFileRoute } from "@tanstack/react-router";
import {
  ComparisonTable,
  MarketingPage,
  Section,
} from "@/components/marketing-page";
import { comparisonHead, type FAQ } from "@/lib/seo";

const PATH = "/vs/athenahq";

const FAQS: FAQ[] = [
  {
    q: "Which covers more AI assistants?",
    a: "AthenaHQ covers up to eight, including Copilot and Grok. Monroya measures ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode.",
  },
  {
    q: "How does Athena's credit model compare?",
    a: "Athena's self-serve plan includes 3,600 monthly credits, with usage drawing them down. Monroya uses flat-rate plans with explicit scan and draft quotas, easier to reason about for a single operator.",
  },
  {
    q: "Is Athena built for the same buyer?",
    a: "Athena's positioning emphasizes 'next-gen marketing teams' and counts brand-level customers like SoFi and Coinbase. Monroya is built for one operator inside a Series A-B company who needs to move the score themselves.",
  },
  {
    q: "Does Athena generate drafts?",
    a: "Athena offers basic AI content optimization. Monroya's draft generation is tied to specific opportunities, find the gap, get the draft, ship.",
  },
  {
    q: "Which is more affordable?",
    a: `AthenaHQ: ${datedPrice("athenahq")}. Monroya starts at $79/month (Starter); its Growth plan is $199/month or $1,990/year. Compare the included scan and draft volumes for your prompt set.`,
  },
  {
    q: "Why would I pick Monroya?",
    a: "Flat-rate pricing instead of credits, six core AI surfaces, and a ranked list that tells you what to do this week.",
  },
  {
    q: "Does either tool detect brand impersonation in AI answers?",
    a: "AthenaHQ has explicit impersonation detection, flagging when an assistant confuses your brand with a similarly-named competitor. Monroya doesn't ship a dedicated impersonation view; its competitor tracking catches misattribution at the prompt level instead.",
  },
  {
    q: "How do the two handle competitor tracking?",
    a: "AthenaHQ tracks competitor share of voice across its full assistant set at the brand level. Monroya tracks competitors per buyer-journey stage and per persona, so you see where a competitor is beating you (e.g. evaluation-stage prompts) rather than just an aggregate mention count.",
  },
  {
    q: "Which one is better for a single marketing operator versus a team?",
    a: "AthenaHQ's UI, credit accounting, and customer list skew toward mid-market and enterprise marketing teams. Monroya is deliberately single-operator: one workspace, one queue, one playbook, no seat math.",
  },
  {
    q: "Can I migrate my prompt set from AthenaHQ to Monroya?",
    a: "Yes. Export your tracked questions from AthenaHQ and add them during onboarding. Historical AthenaHQ results do not transfer; Monroya starts a fresh baseline from its own first scan.",
  },
];

export const Route = createFileRoute("/vs/athenahq")({
  head: () =>
    comparisonHead({
      competitors: ["AthenaHQ"],
      path: PATH,
      breadcrumbLabel: "vs AthenaHQ",
      faqs: FAQS,
    }),
  component: VsAthena,
});

function VsAthena() {
  return (
    <MarketingPage
      checkCta
      stamp="Comparison"
      title="Monroya vs AthenaHQ."
      dek="Broad assistant coverage versus depth and decisive action. Both are valid choices."
      updated="October 2026"
      faqs={FAQS}
      related={[
        { label: "Compare against Profound", to: "/vs/profound" },
        { label: "Compare against Otterly", to: "/vs/otterly" },
        { label: "All alternatives in this category", to: "/vs" },
      ]}
    >
      <Section heading="Side by side">
        <ComparisonTable
          sources={[{ label: "AthenaHQ pricing", url: "https://www.athenahq.ai/pricing" }]}
          checked="October 2026"
          headers={["", "AthenaHQ", "Monroya"]}
          rows={[
            ["Self-serve price", datedPrice("athenahq"), "From $79/mo (Starter); $199/mo or $1,990/yr Growth"],
            ["Pricing model", "Credit-based (3,600/mo self-serve)", "Flat-rate with scan + draft quotas"],
            ["AI surfaces measured", "Up to 8 (ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode, Copilot, Grok, AI Overviews, AI Mode)", "ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews, Google AI Mode"],
            ["Primary view", "AI visibility & citation intelligence", "Buyer-journey matrix + opportunities"],
            ["Content optimization", "Basic AI content optimization", "Draft asset per opportunity"],
            ["Hallucination detection in drafts", "Not surfaced", "Every draft fact-screened for invented stats and version numbers"],
            ["Per-page GEO quick-wins", "Not surfaced", "Per-page audit with one-edit fixes from the site crawl"],
            ["Competitor monitoring", "Yes, including impersonation detection", "Yes, per stage and persona"],
            ["Free option", "Free credits to start", "Free plan"],
          ]}
        />
        <p className="mt-4 text-sm text-muted-foreground">
          Pricing and feature details cross-checked against athenahq.ai,{" "}
          October 2026. Verify on their site for the latest.
        </p>
      </Section>

      <Section heading="What AthenaHQ does well">
        <p>
          Athena's assistant coverage is the broadest in the self-serve
          segment, Copilot and Grok in particular, which most competitors
          either skip or add as enterprise upgrades. Their impersonation
          detection (catching when AI confuses your brand with a similar
          name) is a thoughtful feature most platforms miss. The brand
          customers (SoFi, Coinbase, PagerDuty) speak to the platform's
          credibility with mid-market and enterprise marketing teams.
        </p>
      </Section>

      <Section heading="Where Monroya is different">
        <p>
          We measure six core AI surfaces and repeat direct-assistant checks
          to account for answer variation. We chose flat-rate
          pricing because credit models punish the user who actually uses
          the product. And our home screen is the buyer-journey matrix and
          the opportunity queue, not a citation intelligence dashboard.
        </p>
      </Section>

      <Section heading="Who should choose AthenaHQ">
        <p>
          Your buyers use Copilot or Grok in measurable numbers, you need
          impersonation monitoring, you're comfortable with credit-based
          pricing, and your team is comparing against established
          mid-market platforms.
        </p>
      </Section>

      <Section heading="Who should choose Monroya">
        <p>
          You want flat-rate pricing, six core AI surfaces, a clear view of where in the
          funnel you go missing, and a drafted asset waiting at the end of
          every recommendation.
        </p>
      </Section>
    </MarketingPage>
  );
}
